import { memo, ReactElement, useCallback, useState, ChangeEvent } from 'react';
import { Button, Pagination } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useJobNumbers } from '../hooks/useJobNumbers';
import type { CommittedJobNumberItem, EditingJobNumberItem } from '../types';
import { selectJobNumberPage } from '../utils/selectJobNumberPage';
import JobNumberRow from './JobNumberRow';
import { ListHeader, SectionHeading, JobNumberSearch, EmptyHint, PaginationFooter } from './styles';

interface Props {
  advertisementId: number;
}

const PAGE_SIZE = 5;

const JobNumberList = memo(function JobNumberList({ advertisementId }: Props): ReactElement {
  const { rows, pendingIds, canCommitRow, changeJobNumber, commitRow, removeRow, appendRow } =
    useJobNumbers(advertisementId);
  // 検索語・ページはこのリスト固有の一時的な表示状態のため useState で保持する。
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  const editingRows = rows.filter((row): row is EditingJobNumberItem => row.status === 'editing');
  const committedRows = rows.filter((row): row is CommittedJobNumberItem => row.status === 'committed');

  const { pageRows, currentPage, filteredCount, showSearch, showPagination } = selectJobNumberPage(
    committedRows,
    query,
    page,
    PAGE_SIZE
  );

  const handleSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setPage(1);
  }, []);

  return (
    <>
      <ListHeader>
        <SectionHeading>自動架電対象の仕事No</SectionHeading>
        <Button type='primary' icon={<PlusOutlined />} onClick={appendRow}>
          新規作成
        </Button>
      </ListHeader>
      {showSearch && (
        <JobNumberSearch
          allowClear
          prefix={<SearchOutlined />}
          placeholder='仕事Noで検索'
          value={query}
          onChange={handleSearch}
          aria-label='仕事Noで検索'
        />
      )}
      {editingRows.map(row => (
        <JobNumberRow
          key={row.id}
          item={row}
          canCommit={canCommitRow(row)}
          pending={pendingIds.has(row.id)}
          onChangeJobNumber={changeJobNumber}
          onCommit={commitRow}
          onRemove={removeRow}
        />
      ))}
      {pageRows.map(row => (
        <JobNumberRow
          key={row.id}
          item={row}
          canCommit={false}
          pending={pendingIds.has(row.id)}
          onChangeJobNumber={changeJobNumber}
          onCommit={commitRow}
          onRemove={removeRow}
        />
      ))}
      {showSearch && filteredCount === 0 && <EmptyHint>該当する仕事Noがありません</EmptyHint>}
      {showPagination && (
        <PaginationFooter>
          <Pagination
            current={currentPage}
            pageSize={PAGE_SIZE}
            total={filteredCount}
            onChange={setPage}
            showSizeChanger={false}
            showTotal={total => `全${total.toLocaleString()}件`}
          />
        </PaginationFooter>
      )}
    </>
  );
});

export default JobNumberList;
